import React from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { motion } from 'framer-motion';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Clock, 
  Zap, 
  Send, 
  Headset, 
  Globe, 
  ShieldCheck,
  MessageSquare
} from 'lucide-react';
import { IMAGES } from '@/assets/images';
import { formatPhoneNumber } from '@/lib/index';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';

const contactSchema = z.object({
  name: z.string().min(2, 'Name is too short'),
  email: z.string().email('Invalid email address'),
  phone: z.string().min(10, 'Invalid phone number'),
  serviceType: z.string().min(1, 'Please select a service'),
  supportType: z.string().min(1, 'Please select support type'),
  message: z.string().min(10, 'Please provide more details about your issue'),
});

type ContactFormValues = z.infer<typeof contactSchema>;

const CONTACT_DATA = {
  phone: "8005550199",
  emergencyPhone: "8005550911",
  email: "support@techrepair2026.com",
  address: "123 Tech Avenue, Silicon Suite 400, Innovation City, IC 94043",
  workingHours: "Mon - Fri: 8:00 AM - 8:00 PM",
  coverage: ["Metropolitan Area", "Suburban North", "Suburban South", "Downtown Core", "Tech Park District"]
};

export default function Contact() {
  const {
    register,
    handleSubmit,
    setValue,
    reset,
    watch,
    formState: { errors, isSubmitting },
  } = useForm<ContactFormValues>({
    resolver: zodResolver(contactSchema),
    defaultValues: {
      serviceType: "",
      supportType: "on-site"
    }
  });

  const onSubmit = async (data: ContactFormValues) => {
    // Simulate API call
    await new Promise((resolve) => setTimeout(resolve, 1500));
    console.log('Form submitted:', data);
    toast.success('Service request sent! Our technician will call you shortly.');
    reset();
  };

  return (
    <div className="flex flex-col min-h-screen bg-background">
      {/* Hero Section */}
      <section className="relative h-[40vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0 z-0">
          <img 
            src={IMAGES.CUSTOMER_SUPPORT_3} 
            alt="Customer Support" 
            className="w-full h-full object-cover opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background" />
        </div>
        
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider mb-4">
              <Zap className="w-3 h-3" /> 24/7 Emergency Support
            </span>
            <h1 className="text-4xl md:text-6xl font-bold mb-6">
              Get Expert Help <span className="text-primary">Now</span>
            </h1>
            <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
              Whether it's a broken TV or a network failure, our certified technicians are ready to assist you on-site or via call.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="py-12 md:py-24 container mx-auto px-4">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          
          {/* Left Column: Contact Info */}
          <div className="lg:col-span-5 space-y-8">
            <div className="grid grid-cols-1 gap-6">
              <Card className="border-none shadow-lg bg-primary text-primary-foreground">
                <CardContent className="p-8">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="p-3 bg-white/20 rounded-xl">
                      <Zap className="w-6 h-6 text-white animate-pulse" />
                    </div>
                    <div>
                      <p className="text-sm font-medium opacity-80 uppercase tracking-tighter">Emergency Hotline</p>
                      <h3 className="text-2xl font-bold font-mono">{formatPhoneNumber(CONTACT_DATA.emergencyPhone)}</h3>
                    </div>
                  </div>
                  <p className="text-sm opacity-90">
                    Critical system failure? Our rapid response team is available 24/7 for emergency electronics and IT repairs.
                  </p>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <Card className="border-border/40">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <Phone className="w-8 h-8 text-primary mb-3" />
                    <h4 className="font-bold mb-1">Standard Support</h4>
                    <p className="text-sm text-muted-foreground font-mono">{formatPhoneNumber(CONTACT_DATA.phone)}</p>
                  </CardContent>
                </Card>
                <Card className="border-border/40">
                  <CardContent className="p-6 flex flex-col items-center text-center">
                    <Mail className="w-8 h-8 text-primary mb-3" />
                    <h4 className="font-bold mb-1">Email Us</h4>
                    <p className="text-sm text-muted-foreground">{CONTACT_DATA.email}</p>
                  </CardContent>
                </Card>
              </div>

              <Card className="border-border/40">
                <CardContent className="p-6">
                  <div className="flex gap-4">
                    <MapPin className="w-10 h-10 text-primary shrink-0" />
                    <div>
                      <h4 className="font-bold mb-2">Our Location</h4>
                      <p className="text-sm text-muted-foreground mb-4">{CONTACT_DATA.address}</p>
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Clock className="w-4 h-4" />
                        <span>{CONTACT_DATA.workingHours}</span>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>

              <Card className="border-border/40 overflow-hidden">
                <CardHeader className="bg-accent/50 pb-4">
                  <div className="flex items-center gap-2">
                    <Globe className="w-5 h-5 text-primary" />
                    <CardTitle className="text-lg">Service Coverage</CardTitle>
                  </div>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="flex flex-wrap gap-2">
                    {CONTACT_DATA.coverage.map((area) => (
                      <span key={area} className="px-3 py-1 bg-secondary text-secondary-foreground text-xs rounded-full font-medium">
                        {area}
                      </span>
                    ))}
                  </div>
                  <p className="mt-4 text-xs text-muted-foreground flex items-center gap-1">
                    <ShieldCheck className="w-3 h-3 text-success" /> On-site service available within 30 miles of coverage zones.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Right Column: Contact Form */}
          <div className="lg:col-span-7">
            <Card className="border-border/40 shadow-xl overflow-hidden">
              <CardHeader className="space-y-1 p-8 bg-muted/30">
                <div className="flex items-center gap-3 mb-2">
                  <Headset className="w-6 h-6 text-primary" />
                  <CardTitle className="text-2xl">Request Repair Service</CardTitle>
                </div>
                <CardDescription>
                  Fill out the form below and a technician will contact you within 15 minutes to confirm your appointment.
                </CardDescription>
              </CardHeader>
              <CardContent className="p-8">
                <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="name">Full Name</Label>
                      <Input 
                        id="name" 
                        placeholder="John Doe" 
                        {...register('name')} 
                        className={errors.name ? 'border-destructive' : ''}
                      />
                      {errors.name && <p className="text-xs text-destructive">{errors.name.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email Address</Label>
                      <Input 
                        id="email" 
                        type="email" 
                        placeholder="john@example.com" 
                        {...register('email')} 
                        className={errors.email ? 'border-destructive' : ''}
                      />
                      {errors.email && <p className="text-xs text-destructive">{errors.email.message}</p>}
                    </div>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone Number</Label>
                      <Input 
                        id="phone" 
                        type="tel" 
                        placeholder="(555) 000-0000" 
                        {...register('phone')} 
                        className={errors.phone ? 'border-destructive' : ''}
                      />
                      {errors.phone && <p className="text-xs text-destructive">{errors.phone.message}</p>}
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="serviceType">Service Required</Label>
                      <Select onValueChange={(val) => setValue('serviceType', val)}>
                        <SelectTrigger className={errors.serviceType ? 'border-destructive' : ''}>
                          <SelectValue placeholder="Select a category" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="tv-repair">TV & Display Repair</SelectItem>
                          <SelectItem value="appliances">Home Appliances</SelectItem>
                          <SelectItem value="music-systems">Music & Audio Systems</SelectItem>
                          <SelectItem value="laptop-it">Laptop & Desktop IT</SelectItem>
                          <SelectItem value="network">Internet & Networking</SelectItem>
                          <SelectItem value="other">Other / General Inquiry</SelectItem>
                        </SelectContent>
                      </Select>
                      {errors.serviceType && <p className="text-xs text-destructive">{errors.serviceType.message}</p>}
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label>Support Preference</Label>
                    <div className="grid grid-cols-2 gap-4">
                      <button
                        type="button"
                        onClick={() => setValue('supportType', 'on-site')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          watch('supportType') === 'on-site' 
                          ? 'border-primary bg-primary/5 text-primary' 
                          : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <MapPin className="w-4 h-4" /> On-Site Visit
                      </button>
                      <button
                        type="button"
                        onClick={() => setValue('supportType', 'on-call')}
                        className={`flex items-center justify-center gap-2 p-3 rounded-lg border-2 transition-all ${
                          watch('supportType') === 'on-call' 
                          ? 'border-primary bg-primary/5 text-primary' 
                          : 'border-border hover:border-primary/50'
                        }`}
                      >
                        <Phone className="w-4 h-4" /> On-Call Support
                      </button>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="message">Issue Description</Label>
                    <Textarea 
                      id="message" 
                      placeholder="Tell us what happened with your device..." 
                      className={`min-h-[120px] ${errors.message ? 'border-destructive' : ''}`}
                      {...register('message')}
                    />
                    {errors.message && <p className="text-xs text-destructive">{errors.message.message}</p>}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full h-12 text-lg font-bold group"
                    disabled={isSubmitting}
                  >
                    {isSubmitting ? (
                      <div className="flex items-center gap-2">
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Processing...
                      </div>
                    ) : (
                      <div className="flex items-center gap-2">
                        Send Service Request <Send className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
                      </div>
                    )}
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Live Support Banner */}
      <section className="bg-accent py-16">
        <div className="container mx-auto px-4 text-center">
          <div className="max-w-3xl mx-auto">
            <MessageSquare className="w-12 h-12 text-primary mx-auto mb-6" />
            <h2 className="text-3xl font-bold mb-4">Prefer a quick chat?</h2>
            <p className="text-muted-foreground mb-8 text-lg">
              Our technicians are online and ready to troubleshoot minor issues remotely. Start a live session now.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button variant="outline" size="lg" className="gap-2">
                <Headset className="w-4 h-4" /> Chat with Tech Support
              </Button>
              <Button variant="default" size="lg" className="gap-2" onClick={() => window.location.href = `tel:${CONTACT_DATA.phone}`}>
                <Phone className="w-4 h-4" /> Call Standard Support
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
